import React, { useState, useEffect } from 'react';
import OneClickNeoTrainer from './OneClickNeoTrainer';
import FeatureTiers from './FeatureTiers';
import Branding from './Branding';
import { Link } from 'react-router-dom';

const SimpleNeoTrainer = ({ userTier, hasFeatureAccess, user }) => {
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [neoStatus, setNeoStatus] = useState('none');

    useEffect(() => {
        checkNeoStatus();
    }, []);

    const checkNeoStatus = async () => {
        try {
            const response = await fetch('/api/student/neo/status', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                setNeoStatus(data.neo_status?.has_model ? 'ready' : 'none');
            }
        } catch (error) {
            console.error('Error checking Neo status:', error);
        }
    };

    return (
        <div style={{
            fontFamily: Branding.typography.fontFamily,
            color: Branding.colors.darkSlate,
            backgroundColor: Branding.colors.nearlyWhite,
            padding: Branding.spacing.lg,
            borderRadius: Branding.borders.radius.lg,
            minHeight: '500px'
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: Branding.spacing.xl,
                paddingBottom: Branding.spacing.md,
                borderBottom: `${Branding.borders.width.thin} solid ${Branding.colors.veryLightGray}`
            }}>
                <h1 style={{
                    fontSize: Branding.typography.fontSize['2xl'],
                    fontWeight: Branding.typography.fontWeight.bold,
                    color: Branding.colors.deepPurple,
                    margin: 0
                }}>
                    <span style={{ 
                        color: Branding.colors.vibrantOrange, 
                        fontWeight: Branding.typography.fontWeight.bold
                    }}>🤖</span> Your AI Tutor
                </h1>

                {/* Toggle for advanced users */}
                {hasFeatureAccess('MARKETPLACE_ACCESS') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: Branding.spacing.sm }}>
                        <span style={{ fontSize: Branding.typography.fontSize.sm, color: Branding.colors.slateGray }}>
                            Advanced Mode
                        </span>
                        <label style={{
                            position: 'relative',
                            display: 'inline-block',
                            width: '44px',
                            height: '24px'
                        }}>
                            <input
                                type="checkbox"
                                checked={showAdvanced}
                                onChange={(e) => setShowAdvanced(e.target.checked)}
                                style={{
                                    opacity: 0,
                                    width: 0,
                                    height: 0
                                }}
                            />
                            <span style={{
                                position: 'absolute',
                                cursor: 'pointer',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor: showAdvanced ? Branding.colors.vibrantOrange : Branding.colors.slateGray,
                                transition: '.4s',
                                borderRadius: '24px'
                            }}>
                                <span style={{
                                    position: 'absolute',
                                    content: '',
                                    height: '18px',
                                    width: '18px',
                                    left: showAdvanced ? '23px' : '3px',
                                    bottom: '3px',
                                    backgroundColor: 'white',
                                    transition: '.4s',
                                    borderRadius: '50%'
                                }} />
                            </span>
                        </label>
                    </div>
                )}
            </div>

            {!showAdvanced ? (
                // Simple one-click interface for all users
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        backgroundColor: Branding.colors.pureWhite,
                        borderRadius: Branding.borders.radius.lg,
                        padding: Branding.spacing.xl,
                        boxShadow: Branding.shadows.sm,
                        marginBottom: Branding.spacing.lg
                    }}>
                        <OneClickNeoTrainer />
                    </div>

                    {/* Additional info for different user tiers */}
                    {neoStatus === 'ready' && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: Branding.spacing.md,
                            marginTop: Branding.spacing.xl
                        }}>
                            <div style={{
                                backgroundColor: Branding.colors.pureWhite,
                                borderRadius: Branding.borders.radius.md,
                                padding: Branding.spacing.lg,
                                textAlign: 'center',
                                border: `1px solid ${Branding.colors.tealBlue}`
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: Branding.spacing.sm }}>💬</div>
                                <h3 style={{ 
                                    fontSize: Branding.typography.fontSize.lg,
                                    fontWeight: Branding.typography.fontWeight.semibold,
                                    marginBottom: Branding.spacing.sm
                                }}>
                                    Chat with Neo
                                </h3>
                                <p style={{ 
                                    color: Branding.colors.slateGray,
                                    marginBottom: Branding.spacing.md,
                                    fontSize: Branding.typography.fontSize.sm
                                }}>
                                    Ask your personal AI tutor questions
                                </p>
                                <Link 
                                    to="/tutor"
                                    style={{
                                        backgroundColor: Branding.colors.tealBlue,
                                        color: Branding.colors.pureWhite,
                                        padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                                        borderRadius: Branding.borders.radius.md,
                                        textDecoration: 'none',
                                        fontWeight: Branding.typography.fontWeight.medium,
                                        fontSize: Branding.typography.fontSize.sm
                                    }}
                                >
                                    Start Chatting
                                </Link>
                            </div>

                            <div style={{
                                backgroundColor: Branding.colors.pureWhite,
                                borderRadius: Branding.borders.radius.md,
                                padding: Branding.spacing.lg,
                                textAlign: 'center',
                                border: `1px solid ${Branding.colors.vibrantOrange}`
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: Branding.spacing.sm }}>💰</div>
                                <h3 style={{ 
                                    fontSize: Branding.typography.fontSize.lg,
                                    fontWeight: Branding.typography.fontWeight.semibold,
                                    marginBottom: Branding.spacing.sm
                                }}>
                                    Earn with Bounties
                                </h3>
                                <p style={{ 
                                    color: Branding.colors.slateGray,
                                    marginBottom: Branding.spacing.md,
                                    fontSize: Branding.typography.fontSize.sm
                                }}>
                                    Use Neo to help solve math bounties
                                </p>
                                <Link 
                                    to="/friends" // Friends page includes bounty collaboration
                                    style={{
                                        backgroundColor: Branding.colors.vibrantOrange,
                                        color: Branding.colors.pureWhite,
                                        padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                                        borderRadius: Branding.borders.radius.md,
                                        textDecoration: 'none',
                                        fontWeight: Branding.typography.fontWeight.medium,
                                        fontSize: Branding.typography.fontSize.sm
                                    }}
                                >
                                    View Bounties
                                </Link>
                            </div>

                            <div style={{
                                backgroundColor: Branding.colors.pureWhite,
                                borderRadius: Branding.borders.radius.md,
                                padding: Branding.spacing.lg,
                                textAlign: 'center',
                                border: `1px solid ${Branding.colors.deepPurple}`
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: Branding.spacing.sm }}>👥</div>
                                <h3 style={{ 
                                    fontSize: Branding.typography.fontSize.lg,
                                    fontWeight: Branding.typography.fontWeight.semibold,
                                    marginBottom: Branding.spacing.sm
                                }}>
                                    Train with Friends
                                </h3>
                                <p style={{ 
                                    color: Branding.colors.slateGray,
                                    marginBottom: Branding.spacing.md,
                                    fontSize: Branding.typography.fontSize.sm
                                }}>
                                    Cross-train your Neo with friends' models
                                </p>
                                <Link 
                                    to="/friends"
                                    style={{
                                        backgroundColor: Branding.colors.deepPurple,
                                        color: Branding.colors.pureWhite,
                                        padding: `${Branding.spacing.sm} ${Branding.spacing.md}`,
                                        borderRadius: Branding.borders.radius.md,
                                        textDecoration: 'none',
                                        fontWeight: Branding.typography.fontWeight.medium,
                                        fontSize: Branding.typography.fontSize.sm
                                    }}
                                >
                                    Invite Friends
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                // Advanced interface for power users (marketplace access tier)
                <div style={{
                    backgroundColor: Branding.colors.pureWhite,
                    borderRadius: Branding.borders.radius.lg,
                    padding: Branding.spacing.xl,
                    boxShadow: Branding.shadows.sm
                }}>
                    <div style={{
                        textAlign: 'center',
                        marginBottom: Branding.spacing.xl
                    }}>
                        <h2 style={{
                            fontSize: Branding.typography.fontSize.xl,
                            fontWeight: Branding.typography.fontWeight.semibold,
                            color: Branding.colors.darkSlate,
                            marginBottom: Branding.spacing.sm
                        }}>
                            Advanced Training Interface
                        </h2>
                        <p style={{
                            color: Branding.colors.slateGray,
                            fontSize: Branding.typography.fontSize.sm
                        }}>
                            Configure advanced training parameters and neural network visualization
                        </p>
                    </div>

                    {/* Link to advanced AITrainer */}
                    <div style={{ textAlign: 'center' }}>
                        <Link 
                            to="/trainer/advanced"
                            style={{
                                backgroundColor: Branding.colors.deepPurple,
                                color: Branding.colors.pureWhite,
                                padding: `${Branding.spacing.md} ${Branding.spacing.xl}`,
                                borderRadius: Branding.borders.radius.md,
                                textDecoration: 'none',
                                fontWeight: Branding.typography.fontWeight.medium,
                                fontSize: Branding.typography.fontSize.lg,
                                display: 'inline-block'
                            }}
                        >
                            🔬 Launch Advanced Trainer
                        </Link>
                    </div>

                    {/* Quick options for advanced users */}
                    <div style={{
                        marginTop: Branding.spacing.xl,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: Branding.spacing.md
                    }}>
                        <div style={{
                            padding: Branding.spacing.md,
                            border: `1px solid ${Branding.colors.veryLightGray}`,
                            borderRadius: Branding.borders.radius.md,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ fontSize: Branding.typography.fontSize.md, marginBottom: Branding.spacing.sm }}>
                                📊 Learning Analytics
                            </h4>
                            <p style={{ fontSize: Branding.typography.fontSize.sm, color: Branding.colors.slateGray }}>
                                Detailed training metrics and performance analysis
                            </p>
                        </div>

                        <div style={{
                            padding: Branding.spacing.md,
                            border: `1px solid ${Branding.colors.veryLightGray}`,
                            borderRadius: Branding.borders.radius.md,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ fontSize: Branding.typography.fontSize.md, marginBottom: Branding.spacing.sm }}>
                                🧠 Neural Network Viz
                            </h4>
                            <p style={{ fontSize: Branding.typography.fontSize.sm, color: Branding.colors.slateGray }}>
                                Real-time visualization of learning processes
                            </p>
                        </div>

                        <div style={{
                            padding: Branding.spacing.md,
                            border: `1px solid ${Branding.colors.veryLightGray}`,
                            borderRadius: Branding.borders.radius.md,
                            textAlign: 'center'
                        }}>
                            <h4 style={{ fontSize: Branding.typography.fontSize.md, marginBottom: Branding.spacing.sm }}>
                                ⚙️ Custom Parameters
                            </h4>
                            <p style={{ fontSize: Branding.typography.fontSize.sm, color: Branding.colors.slateGray }}>
                                Fine-tune learning styles and training methods
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimpleNeoTrainer;